<template>
  <v-form v-model="valid" ref="form">
    <div class="form-container px-10">
       <div class="form-group text-center">
        <h1>{{title}}</h1>
        <h3 class="form-type">Register</h3>
      </div>
      <div class="form-group">
        <v-row v-show="!verifyStep">
          <v-col class="py-0 px-0 ml-3 mr-1">
            <v-text-field
              :disabled="verifyStep"
              tabindex="1"
              outlined
              dense
              label="First name *"
              v-model="firstName"
              :rules="[global.rules.requiredField]"
              />
          </v-col>
          <v-col class="py-0 px-0 mr-3 ml-1">
            <v-text-field
              :disabled="verifyStep"
              tabindex="2"
              outlined
              dense
              label="Last name *"
              v-model="lastName"
              :rules="[global.rules.requiredField]"
              />
          </v-col>
        </v-row>
      </div>
      <div class="form-group">
        <v-text-field
          :disabled="emailCodeValidated"
          tabindex="3"
          outlined
          dense
          label="Email address *"
          class="mt-n1"
          v-model="email"
          :rules="[global.rules.email]"
          />
          <v-row v-if="showEmailVerification" class="pa-3 text-center">
            <span class="mt-1 mr-4">Code:</span>
            <CodeInput
              ref="mailCodeRef"
              tabindex="15"
              :fieldWidth="32"
              :fieldHeight="32"
              :loading="false"
              class="input"
              v-on:change="onChangeEmailCode"/>
            <div class="resend-code-btn"><a @click="resendVerificationCodes('email')">Resend Code</a></div>
          </v-row>
      </div>
      <div v-show="!verifyStep" class="form-group">
        <v-text-field
          :disabled="verifyStep"
          tabindex="5"
          outlined
          dense
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :rules="[global.rules.passwordField]"
          :type="showPassword ? 'text' : 'password'"
          label="Password *"
          @click:append="showPassword = !showPassword"
          class="mt-n1 password-field"
          @focus="passwordFocus = true" 
          @blur="passwordFocus = false"
          v-model="password"/>
      </div>
      <passwordRules :showRules="showRules" :password="password" />
      <div v-show="!verifyStep" class="form-group">
        <v-text-field
          :disabled="verifyStep"
          tabindex="6"
          outlined
          dense
          :append-icon="showConfirm ? 'mdi-eye' : 'mdi-eye-off'"
          :rules="[passwordMatching]"
          :type="showConfirm ? 'text' : 'password'"
          label="Confirm password *"
          @click:append="showConfirm = !showConfirm"
          v-model="passwordVerification"
          class="mt-n1"
          />
      </div>
        <v-checkbox
        v-model="legalAgreed"
        tabindex="7"
        class="agree-check mt-n1"
        v-if="!verifyStep"
        hide-details>
          <div slot='label'>I agree to
            <a href="#" @click.stop.prevent="openLegalDoc('/api/1/admin/legal/terms_and_conditions')">Terms and Conditions</a>,
            <a href="#" @click.stop.prevent="openLegalDoc('/api/1/admin/legal/privacy_policy')">Privacy Policy</a>,
            <a href="#" @click.stop.prevent="openLegalDoc('/api/1/admin/legal/cookie_policy')">Cookie Policy</a>, and
            <a href="#" @click.stop.prevent="openLegalDoc('/api/1/admin/legal/acceptable_use_policy')">Acceptable use Policy</a>
          </div>
        </v-checkbox>
      <div class="form-group mt-2">
        <v-btn
          tabindex="8"
          color="primary"
          v-on:click="registerButtonOnClick"
          block
          :loading="btnLoading"
          v-if="!verifyStep"
          :disabled="!valid">Register</v-btn>
        <div class="form-group text-center down-link mt-1">
          <span>You already have an account?<a style="margin-left: 5px" @click="AuthNavigateTo('gotoLogin')">Login</a></span>
        </div>
      </div>
    </div>
  </v-form>
</template>

<script>
import RegisterStrings from '../views/Profile/RegisterStrings'
import AuthPagesMixin from "../mixins/AuthPagesMixin"
import CodeInput from "vue-verification-code-input"
import passwordRules from "../Utils/passwordRules.vue"

export default {
  name: 'RegisterForm',
  mixins: [AuthPagesMixin],
  components: { CodeInput, passwordRules},
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      validatedEmail: "",
      emailCode: "",
      emailCodeValidated: false,
      password: "",
      passwordVerification: "",
      timeout: 10000,
      verifyStep: false,
      showIcon: false,
      showConfirm: false,
      showPassword: false,
      showEmailVerification: false,
      updatingEmail: false,
      valid: true,
      legalAgreed: false,
      legalDocsVersions: {},
      btnLoading: false,
      passwordFocus: false
    }
  },
  watch: {
    emailCode: function (newVal) {
      if (newVal.length == 6) {
        this.axios
          .post("/register/emailconfirmation", {
            emailcode: newVal,
          })
          .then(() => {
            this.emailCodeValidated = true
            this.showEmailVerification = false
          })
          .catch((ctx) => {
            this.ShowInfo('error', RegisterStrings(this, ctx.response.data.error) || 'Error validating email')
          })
      }
    },
    emailCodeValidated: function () {
      this.finishRegistration()
    },
  },
  computed: {
    title() {
      return window.location.hostname.split(".").splice(1).join(".")
    },
    emailChanged(){
      return this.email != this.validatedEmail
    },
    passwordMatching() {
      return this.passwordVerification == this.password || "Passwords don't match"
    },
    showRules() {
      return this.passwordFocus || (this.password && !this.global.passwordRegex.test(this.password))
    }
  },
  methods: {
    response: function (response) {
      let msg = ""
      if (response && response.status == 422 && response.data) {
        switch (response.data.error) {
        case "invalid_first_name":
        case "invalid_last_name":
        case "invalid_email_format":
        case "email_already_used":
        case "invalid_phonenumber":
        case "phone_already_used":
        case "invalid_password":
        case "invalid_email_code":
        case "invalid_sms_code":
          msg = RegisterStrings(this, response.data.error)
          break
        default:
          msg = RegisterStrings(this, "something_went_wrong")
        }
      } else if (response && response.status > 400) {
        msg = RegisterStrings(this, "something_went_wrong")
      }
      this.ShowInfo('error', msg)
    },
    finishRegistration: function () {
      if (this.emailCodeValidated) {
        var data = {
          firstname: this.firstName,
          lastname: this.lastName,
          email: this.email,
          password: this.password,
          redirectparams: "/",
          langkey: "en",
        }
        this.axios
          .post(`/register${window.location.search}`, data)
          .then((response) => {
            if (response.data && response.data.redirecturl && response.data.redirecturl != "/") {
              window.location.href = response.data.redirecturl
            } else {
              this.login()
            }
            console.log(response)
          })
          .catch((ctx) => {
            this.ShowInfo('error', ctx.response)
          })
      }
    },
    showVerifyStep() {
      this.verifyStep = true
      this.showEmailVerification = true
      this.ShowInfo("info", RegisterStrings(this, "validate_email_and_phonenumber"))
      this.$nextTick(()=> {
        this.$refs.mailCodeRef.$el.children[0].focus()
      })
    },
    attachDocs(data) {
      if(this.isDevEnv() || !this.legalDocsVersions || !this.legalDocsVersions.privacy_policy){
        data['acceptance_timestamp'] = Math.floor((new Date()).getTime()/1000)
        data['privacy_policy_version'] = 1
        data['cookie_policy_version'] = 1
        data['acceptable_use_policy_version'] = 1
        data['terms_and_conditions_version'] = 1
      } else {
        data['acceptance_timestamp'] = Math.floor((new Date()).getTime()/1000)
        data['privacy_policy_version'] = this.legalDocsVersions.privacy_policy.current_version
        data['cookie_policy_version'] = this.legalDocsVersions.cookie_policy.current_version
        data['acceptable_use_policy_version'] = this.legalDocsVersions.acceptable_use_policy.current_version
        data['terms_and_conditions_version'] = this.legalDocsVersions.terms_and_conditions.current_version
      }
      return data
    },
    registerButtonOnClick() {
      if (!this.$refs.form.validate()) {
        return
      }
      if(!this.legalAgreed){
        this.ShowInfo('Error', RegisterStrings(this, "policies_not_agreed"))
        return
      }
      if (!this.verifyStep) {
        if (this.password != this.passwordVerification) {
          this.ShowInfo('Error', RegisterStrings(this, "passwords_do_not_match"))
          return
        }
        this.btnLoading = true
        this.axios
          .post("/register/validation",this.attachDocs(this.getValidationData()))
          .then(() => {
            this.validatedEmail = this.email
            this.showVerifyStep()
          })
          .catch((ctx) => {
            this.response(ctx.response)
          }).finally(()=>{
            this.btnLoading = false
          })
      }
    },
    updateEmail() {
      this.updatingEmail = true
      this.axios
        .post(
          `/register${window.location.search}`,
          this.attachDocs(this.getValidationData(this.email)))
        .then(() => {
          this.validatedEmail = this.email
          this.ShowInfo('info', RegisterStrings(this, "email_updated"))
        })
        .catch((ctx) => {
          this.response(ctx.response)
        })
        .finally(()=> {
          this.updatingEmail = false
        })
    },
    resendVerificationCodes(entity){
      if(entity == 'email' && this.emailChanged){
        this.updateEmail()
        return
      }
      this.axios
        .post(
          "/register/resendvalidation",
          this.getValidationData()
        )
        .then(() => {
          this.ShowInfo('info', RegisterStrings(this, `${entity}_resent`))
        })
        .catch((ctx) => {
          this.response(ctx.response)
        })
    },
    getValidationData(email) {
      return {
        firstname: this.firstName,
        lastname: this.lastName,
        email: email || this.email,
        langkey: "en",
        password: this.password,
      }
    },
    reset() {
      this.$refs.form.reset()
    },
    onChangeEmailCode(v) {
      this.emailCode = v
    },
    openLegalDoc(doc){
      window.open(doc, '_blank')
    }
  },
  created(){
    this.axios.get(
      "/api/1/admin/legal",
    ).then((response) => {
      this.legalDocsVersions = response.data
    }) .catch((ctx) => {
      this.ShowInfo('info', ctx)
    })
  }
}
</script>

<style>

.form-container{
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  min-height: 500px;
  background-color: white;
}
.slider-img{
  width: 225px;
  height: auto;
  margin: auto;
  display: block;
}
.form-type{
  color: grey;
}
.down-link{
  font-size: 14px;
}
.resend-code-btn{
  font-size: 14px;
  width: 100%;
}
.agree-check label{
  font-size: 12px;
}
.password-field .v-text-field__details {
  display: none;
}
</style>